import {
  classifyInstallationTypology,
  declaredSurfaceActivity,
  findReadingElement,
  heightCandidates,
  isSuspendedTypology,
  planDesignReviewInputSchema,
  summarizeEnvironmentReading,
  verticalClearanceRule,
  type ActivityConfidence,
  type AgentContext,
  type AgentMessage,
  type EnvironmentReading,
  type PlanDesignReviewInput,
  type TaskPlanProposal,
} from "@calcplan/ai-capabilities";
import { describeComponentry } from "./ai-element-componentry";

export type PlanDesignReviewBrief = NonNullable<NonNullable<AgentContext["orchestration"]>["planReview"]>;

/**
 * Quem é, de fato, o elemento que a decisão aponta: nome, tipologia, onde está
 * e o que a árvore de componentes registra. O revisor confere isto contra o
 * descritor do pedido ("móvel de 3 portas") — a identidade do alvo é o
 * primeiro item do parecer, antes de qualquer cota.
 */
function describeReadingElementIdentity(reading: EnvironmentReading, id: string | null | undefined, name: string | null | undefined): string | null {
  const element = (id ? reading.elements.find((candidate) => candidate.elementId === id) : null)
    ?? (name ? findReadingElement(reading, name) : null);
  if (!element) return null;
  const placement = element.suspended
    ? `suspenso, base a ${element.bottomMm} mm`
    : `${element.mount === "floor" ? "no piso" : element.mount}, topo a ${element.topMm} mm`;
  return [
    `${element.name} (${element.typology.label}; ${placement}; ${element.widthMm} × ${element.depthMm} mm${element.wallId ? `; ${element.wallId}` : ""})`,
    `componentes: ${describeComponentry(element.componentry ?? null)}`,
  ].join(" · ").slice(0, 300);
}

/**
 * A prancheta passa por um segundo projetista antes da execução sempre que o
 * plano carrega decisões de autoria do agente: é onde o estúdio pega o
 * "cada escolha legal, conjunto sem âncora" que nenhuma régua determinística
 * enxerga. Autoria estrita não tem o que revisar — o projetista já decidiu tudo.
 */
export function planDesignReviewRequired(proposal: TaskPlanProposal, designFreedom: string): boolean {
  if (designFreedom === "strict") return false;
  const agentDecides = proposal.decisionAuthorities.some((decision) => decision.authority === "agent")
    || (proposal.designResolutions ?? []).some((resolution) => resolution.authority === "agent");
  return agentDecides && (proposal.spatialDecisions.length > 0 || (proposal.designResolutions ?? []).length > 0);
}

/** O brief sanitizado do revisor: decisões com razões, leituras e candidatos — sem o histórico do planejador. */
export function buildPlanDesignReviewBrief(input: {
  objective: string;
  designFreedom: string;
  proposal: TaskPlanProposal;
  reading: EnvironmentReading;
  pendingObjections?: ReadonlyArray<string>;
}): PlanDesignReviewBrief {
  const { proposal, reading } = input;
  const assumptions = proposal.assumptions ?? [];
  const candidateGroups: PlanDesignReviewBrief["heightCandidates"] = [];
  for (const decision of proposal.spatialDecisions) {
    if (decision.relation !== "above" && decision.relation !== "below") continue;
    const subjectTypology = classifyInstallationTypology({ name: decision.subject });
    const suspended = decision.support === "wall" || decision.support === "ceiling" || isSuspendedTypology(subjectTypology.kind);
    if (!suspended) continue;
    const reference = (decision.referenceElementId
      ? reading.elements.find((candidate) => candidate.elementId === decision.referenceElementId) ?? null
      : null) ?? findReadingElement(reading, decision.reference);
    if (!reference) continue;
    const surface = reading.surfaces.find((entry) => entry.elementId === reference.elementId) ?? null;
    const declaredUse = declaredSurfaceActivity(assumptions, [decision.reference, reference.name]);
    const pairRule = verticalClearanceRule({
      upper: subjectTypology.kind,
      lower: reference.typology.kind,
      relation: "above",
      support: decision.support,
    });
    const height = decision.bottomHeightMm != null && decision.topHeightMm != null
      ? decision.topHeightMm - decision.bottomHeightMm
      : null;
    const candidates = heightCandidates({
      reading,
      reference,
      subjectKind: subjectTypology.kind,
      subjectHeightMm: height,
      activity: declaredUse?.activity ?? surface?.activity ?? "unknown",
      activityConfidence: declaredUse ? "declared" : ((surface?.confidence ?? "low") as ActivityConfidence),
      pairBottomHeightMm: pairRule?.bottomHeightMm ?? null,
      pairClearanceMm: pairRule?.typicalMm ?? null,
      pairMinimumMm: pairRule?.minimumMm ?? null,
    });
    candidateGroups.push({
      subject: decision.subject.slice(0, 160),
      candidates: candidates.slice(0, 10).map((candidate) => [
        `${candidate.label}: base ${candidate.bottomHeightMm} mm`,
        candidate.topHeightMm !== null ? `topo ${candidate.topHeightMm} mm` : null,
        candidate.clearanceMm !== null ? `vão ${candidate.clearanceMm} mm sobre ${reference.name}` : null,
        candidate.reach,
        candidate.notes.length ? candidate.notes.join("; ") : null,
      ].filter(Boolean).join(" · ").slice(0, 400)),
    });
    if (candidateGroups.length >= 8) break;
  }
  // Nenhum corte silencioso: o revisor sabe exatamente o que o brief não
  // coube, em contagens — planos de ambiente grande são maiores que a amostra.
  const omitted: string[] = [];
  const totalResolutions = (proposal.designResolutions ?? []).length;
  if (totalResolutions > 24) omitted.push(`resoluções: ${totalResolutions - 24} de ${totalResolutions} não listadas (mostradas as 24 primeiras)`);
  const fullSummary = summarizeEnvironmentReading(reading);
  if (fullSummary.length > 6_000) omitted.push("resumo do ambiente truncado em 6.000 caracteres");
  const verticalDecisionCount = proposal.spatialDecisions.filter((decision) => decision.relation === "above" || decision.relation === "below").length;
  if (verticalDecisionCount > candidateGroups.length && candidateGroups.length >= 8) {
    omitted.push(`candidatos de altura: ${verticalDecisionCount - candidateGroups.length} decisões verticais sem grupo listado`);
  }
  return {
    originalObjective: input.objective.slice(0, 2_000),
    designFreedom: input.designFreedom as PlanDesignReviewBrief["designFreedom"],
    environmentSummary: fullSummary.slice(0, 6_000),
    decisions: proposal.spatialDecisions.slice(0, 16).map((decision) => ({
      subject: decision.subject.slice(0, 160),
      relation: decision.relation,
      reference: decision.reference.slice(0, 160),
      support: decision.support,
      bottomHeightMm: decision.bottomHeightMm ?? null,
      topHeightMm: decision.topHeightMm ?? null,
      clearanceMm: decision.clearanceMm ?? null,
      datum: decision.datum
        ? {
            kind: decision.datum.kind,
            ...(decision.datum.reference ? { reference: decision.datum.reference.slice(0, 160) } : {}),
            ...(decision.datum.justification ? { justification: decision.datum.justification.slice(0, 500) } : {}),
          }
        : null,
      rationale: decision.rationale.slice(0, 600),
      ...(() => {
        const referenceIdentity = describeReadingElementIdentity(reading, decision.referenceElementId, decision.reference);
        const subjectIdentity = describeReadingElementIdentity(reading, decision.subjectElementId, null);
        return {
          ...(referenceIdentity ? { referenceIdentity } : {}),
          ...(subjectIdentity ? { subjectIdentity } : {}),
        };
      })(),
    })),
    resolutions: (proposal.designResolutions ?? []).slice(0, 24).map((resolution) => ({
      subject: resolution.subject.slice(0, 160),
      attribute: resolution.attribute,
      decision: resolution.decision.slice(0, 500),
      source: resolution.source,
    })),
    assumptions: assumptions.slice(0, 12).map((assumption) => ({
      topic: assumption.topic,
      assumption: assumption.assumption.slice(0, 300),
      ...(assumption.subject ? { subject: assumption.subject.slice(0, 160) } : {}),
      ...(assumption.surfaceActivity ? { surfaceActivity: assumption.surfaceActivity } : {}),
    })),
    heightCandidates: candidateGroups,
    pendingObjections: (input.pendingObjections ?? []).slice(0, 6).map((issue) => issue.slice(0, 400)),
    omitted: omitted.slice(0, 4),
  };
}

/** A mensagem única da chamada de revisão: a instrução mora no prompt de sistema; o brief, no contexto. */
export function planDesignReviewMessages(): AgentMessage[] {
  return [{
    role: "user",
    content: [{
      type: "text",
      text: "Revise a prancheta descrita em orchestration.planReview e registre o parecer com task.plan.review.",
    }],
  }];
}

export interface PlanDesignReviewOutcome {
  verdict: "sign" | "object";
  grounded: boolean;
  summary: string;
  observations: string[];
  objection: PlanDesignReviewInput["objection"] | null;
  raw: PlanDesignReviewInput;
}

const GROUNDING_MEASURE = /\d[\d.,]*\s*mm/i;

function normalizeText(value: string): string {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLocaleLowerCase("pt-BR");
}

/**
 * Valida o parecer e aplica a disciplina do revisor: objeção sem âncora nas
 * leituras (uma medida em mm, ou o nome de um elemento lido) é rebaixada a
 * observação — o mesmo contrato do crítico visual, agora na prancheta.
 */
export function planDesignReviewOutcome(input: unknown, reading: EnvironmentReading | null): PlanDesignReviewOutcome | null {
  const parsed = planDesignReviewInputSchema.safeParse(input);
  if (!parsed.success) return null;
  const verdict = parsed.data;
  if (verdict.verdict === "sign" || !verdict.objection) {
    return { verdict: "sign", grounded: true, summary: verdict.summary, observations: verdict.observations, objection: null, raw: verdict };
  }
  const grounding = normalizeText(verdict.objection.grounding);
  const grounded = GROUNDING_MEASURE.test(verdict.objection.grounding)
    || (reading?.elements ?? []).some((element) => element.name.length >= 4 && grounding.includes(normalizeText(element.name)));
  if (!grounded) {
    return {
      verdict: "sign",
      grounded: false,
      summary: verdict.summary,
      observations: [...verdict.observations, `Objeção sem âncora rebaixada a observação: ${verdict.objection.grounding}`].slice(0, 6),
      objection: null,
      raw: verdict,
    };
  }
  return { verdict: "object", grounded: true, summary: verdict.summary, observations: verdict.observations, objection: verdict.objection, raw: verdict };
}
